import { CompanyItem, emailValidator, isOk, ok, PhoneNumber } from "@/common/common";
import {
  DCEvent,
  EventDispatcher,
  navigate,
  showError,
  showGeneralError,
  showGeneralSuccess,
} from "@/events/eventsDomain";
import { compact, max } from "lodash";
import { companyRoleLabel } from "@/utils/Roles";
import {
  extractPortfolioContactsUrl,
  extractPortfolioShareholderUrl,
  portfolioApi,
  SelectedPortfolioOption,
} from "@/portfolio/portfolioDomain";
import { formatPhoneNumber } from "@/utils/phoneNumbers";
import { TabProps } from "@/components-v2/Tabs";
import { isBlank, isNotBlank } from "@/utils/strings";
import { InfoTagProps } from "@/components-v2/InfoTag";
import { companySettingsApi } from "@/views/CompanySettings/CompanySettingsApi";
import { DataTableProps, TableColumn, TableColumnValue } from "@/components-v2/DataTable";
import {
  DropdownMenuItemProps,
  DropdownMenuItems,
  DropdownMenuProps,
} from "@/components-v2/Dropdown";
import {
  isOwnShareholder,
  totalNumberOfSharesOfShareholder,
  totalNumberOfVotesForShareholder,
} from "@/types/Shareholder";
import { when } from "@/utils/functions";
import { LinkButtonProps } from "@/components-v2/Button";
import {
  AggregatedCompanyItem,
  AggregatedContactItem,
  AggregatedShareSummary,
  ContactsState,
  EditDetailsFieldDialogState,
  extractAggregatedCompanyRoleDetailsUrl,
  extractAggregatedShareholderDetailsUrl,
  initContactsPage,
  InvestmentStatus,
  OwnershipItem,
  RoleItem,
  updateContactsState,
} from "./contactsDomain";
import { formatNumber, formatPercent, percentOf } from "@/utils/numbers";
import { Language } from "@/i18n/languages";
import { InfoButtonProps } from "@/components-v2/InfoButton";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { hasShares } from "@/stores/ShareholdersStore";
import { toDate } from "@/utils/Dates";
import { TMessage } from "@/i18n/tt";
import { getFullName, validateName } from "@/utils/Roles";
import { toCompanyRoleReference, toShareholderReference } from "@/utils/UrnReference";
import { IconName } from "@/components-v2/iconMapping";
import { PhoneInputModel } from "@/components-v2/PhoneInput";
import { ShareType, ShareTypeCode, shareTypeLabel } from "@/common/share";
import { adminRoles } from "@/utils/navigation.ts";
import {
  RoleForm,
  RolesDialogsProps,
  RolesDialogState,
} from "@/companyRoles/companyRolesDialogsDomain.ts";
import { CompanyAccess, roleForCompany } from "@/common/customRole";
import { createElement } from "react";

export type AggregatedContactDetailsTab = "companyRole" | "shareholder";

export type PersonInfoField =
  | { type: "phone"; updateModel: PhoneInputModel }
  | { type: "personName"; updateModel: StringFieldModel }
  | { type: "email"; updateModel: StringFieldModel }
  | { type: "contactInfoName"; updateModel: StringFieldModel }
  | { type: "contactInfoNationalId"; updateModel: StringFieldModel };

export const aggregatedDetailsFieldTypes = [
  "personName",
  "contactInfoName",
  "contactInfoNationalId",
  "email",
  "phone",
] as const;
export type AggregatedDetailsFieldType = (typeof aggregatedDetailsFieldTypes)[number];

export const aggregatedDetailsFieldTypeLabel: Record<AggregatedDetailsFieldType, TMessage> = {
  personName: { key: "contacts.details.edit.menu.name" },
  email: { key: "contacts.details.edit.menu.email" },
  phone: { key: "contacts.details.edit.menu.phone" },
  contactInfoName: { key: "contacts.details.edit.menu.representative-name" },
  contactInfoNationalId: { key: "contacts.details.edit.menu.representative-national-id" },
};

type FieldConfigItemProps = {
  id: string;
  label: TMessage;
  icon: IconName;
};

export const fieldMenuItem: Record<AggregatedDetailsFieldType, FieldConfigItemProps> = {
  personName: {
    id: "personName",
    label: aggregatedDetailsFieldTypeLabel["personName"],
    icon: "user-list",
  },
  email: {
    id: "email",
    label: aggregatedDetailsFieldTypeLabel["email"],
    icon: "envelope-simple-open",
  },
  phone: {
    id: "phone",
    label: aggregatedDetailsFieldTypeLabel["phone"],
    icon: "phone",
  },
  contactInfoName: {
    id: "contactInfoName",
    label: aggregatedDetailsFieldTypeLabel["contactInfoName"],
    icon: "user-list",
  },
  contactInfoNationalId: {
    id: "contactInfoNationalId",
    label: aggregatedDetailsFieldTypeLabel["contactInfoNationalId"],
    icon: "identification-badge",
  },
};

export type AggregatedContactDetailsPageProps = {
  navigateBack: () => void;
  name?: string;
  tabs: TabProps[];
  table: DataTableProps<RoleItem> | DataTableProps<OwnershipItem>;
  infoTags?: InfoTagProps[];
  lastLogin?: string;
  editDialog?: RolesDialogsProps;
  searchFieldModel: StringFieldModel;
  investmentStatusModel?: RadioFieldModel<InvestmentStatus>;
  aggregatedContactDetailsOptionsMenu?: DropdownMenuProps;
};

export type EditDetailsFieldDialogProps = {
  title: TMessage;
  submitLabel: TMessage;
  isUpdating?: boolean;
  isSubmitDisabled?: boolean;
  detailsFieldModel: PersonInfoField;
  aggregatedDetailsFieldType: AggregatedDetailsFieldType;
  selectedCompanyModel: OptionFieldModel<AggregatedCompanyItem>;
  alertMessage?: TMessage;
  onClose: () => void;
  updateContactsPersonalField: () => void;
  toggleCompany: (item: AggregatedCompanyItem) => void;
  clearCompanies: () => void;
  selectAllCompanies: () => void;
};

export type UpdatePersonField = {
  field: AggregatedDetailsFieldType;
  value: string;
  countryCode?: string;
  roles: string[];
};

export const navigateToAggregatedCompanyRoleDetails = ({
  id,
  portfolio,
}: {
  id: string | number;
  portfolio: SelectedPortfolioOption;
}): DCEvent[] => {
  return [{ type: "navigate", url: extractAggregatedCompanyRoleDetailsUrl(portfolio, id) }];
};

export const prepAggregatedContactDetailsPage = ({
  dispatch,
  portfolio,
  contacts,
  id,
  language,
  tab,
  access,
}: {
  dispatch: EventDispatcher;
  portfolio: SelectedPortfolioOption;
  contacts?: ContactsState;
  id: string;
  language: Language;
  tab: AggregatedContactDetailsTab;
  access: CompanyAccess;
}): AggregatedContactDetailsPageProps => {
  const items = isOk(contacts?.response) ? contacts.response.data.contacts : [];
  const shareSummaries = isOk(contacts?.response) ? contacts.response.data.shareSummaries : [];
  const aggregate = items.find((item) =>
    tab === "companyRole"
      ? item.roleItems.some((r) => String(r.role.companyroleId) === id)
      : item.ownershipItems.some((o) => String(o.shareholder.id) === id),
  );
  const shareTypes = isOk(contacts?.response)
    ? filterShareTypesByOwnership(contacts.response.data.shareTypes, aggregate?.ownershipItems)
    : [];
  const roleItem = aggregate?.roleItems.find((i) => String(i.role.companyroleId) === id);

  const navigateToBoardMembers = (item: RoleItem) =>
    dispatch([{ type: "navigate", url: adminRoles(item.company.organisationNumber, "current") }]);

  function openEditDialog(row: RoleItem) {
    return dispatch([
      updateContactsState(() => ({
        form: new RoleForm(row.role),
      })),
    ]);
  }

  function onNavigateToShareholder(shareholderId: number) {
    return dispatch([
      {
        type: "navigate",
        url: extractPortfolioShareholderUrl(portfolio, shareholderId),
      },
    ]);
  }

  return {
    navigateBack: () =>
      dispatch([
        navigate(
          tab === "shareholder"
            ? `${extractPortfolioContactsUrl(portfolio)}/shareholders`
            : `${extractPortfolioContactsUrl(portfolio)}/board`,
        ),
      ]),
    name: aggregate?.name,
    tabs: prepContactDetailsTabs({
      dispatch,
      roleItems: aggregate?.roleItems ?? [],
      ownershipItems: aggregate?.ownershipItems ?? [],
      portfolio,
      tab,
    }),
    table: prepContactDetailsTable({
      tab,
      aggregated: aggregate,
      openEditDialog,
      navigateToBoardMembers,
      onNavigateToShareholder,
      language,
      shareSums: shareSummaries,
      filter: contacts?.detailsSearchFieldModel?.inputValue ?? "",
      investmentStatus: contacts?.investmentStatusModel?.value ?? "current",
      shareTypes,
    }),
    editDialog: prepEditDialog({
      dispatch,
      mainItem: roleItem,
      state: contacts,
      portfolio,
    }),
    infoTags: aggregate ? prepInfoTags(aggregate) : undefined,
    lastLogin: lastLogin(aggregate),
    searchFieldModel: contacts?.detailsSearchFieldModel ?? new StringFieldModel(),
    investmentStatusModel: tab === "shareholder" ? contacts?.investmentStatusModel : undefined,
    aggregatedContactDetailsOptionsMenu: aggregate
      ? prepAggregatedContactDetailsDropdownMenu({
          dispatch,
          aggregate,
          isUpdating: contacts?.editDetailsFieldDialogState?.isUpdating,
          access,
        })
      : undefined,
  };
};

export const prepEditAggregatedDetailsDialog = (
  editDialogState: EditDetailsFieldDialogState,
  dispatch: EventDispatcher,
  portfolio: SelectedPortfolioOption,
): EditDetailsFieldDialogProps => {
  return {
    title: editDialogState.title,
    submitLabel: editDialogState.submitLabel,
    isUpdating: editDialogState.isUpdating,
    detailsFieldModel: editDialogState.detailsFieldModel,
    aggregatedDetailsFieldType: editDialogState.aggregatedDetailsFieldType,
    selectedCompanyModel: editDialogState.selectedCompanyModel,
    isSubmitDisabled: editDialogState.isSubmitDisabled,
    alertMessage: editDialogState.alertMessage,
    onClose: () =>
      dispatch([
        updateContactsState(() => ({
          editDetailsFieldDialogState: undefined,
        })),
      ]),
    updateContactsPersonalField: () =>
      onUpdateContactsPersonalField({ dispatch, editDialogState, portfolio }),
    toggleCompany: (item) => {
      editDialogState.selectedCompanyModel.toggle(item);
      dispatch([
        updateContactsState(() => ({
          editDetailsFieldDialogState: {
            ...editDialogState,
            submitLabel: detailsFieldSubmitLabel(
              editDialogState.selectedCompanyModel.selected.length,
            ),
            isSubmitDisabled: editDialogState.selectedCompanyModel.selected.length === 0,
            alertMessage:
              editDialogState.selectedCompanyModel.selected.length === 0
                ? {
                    key: "contacts.company-roles.details-alert-message",
                  }
                : undefined,
          },
        })),
      ]);
    },
    selectAllCompanies: () => {
      editDialogState.selectedCompanyModel.selectAll();
      dispatch([
        updateContactsState(() => ({
          editDetailsFieldDialogState: {
            ...editDialogState,
            submitLabel: detailsFieldSubmitLabel(
              editDialogState.selectedCompanyModel.selected.length,
            ),
            isSubmitDisabled: false,
            alertMessage: undefined,
          },
        })),
      ]);
    },
    clearCompanies: () => {
      editDialogState.selectedCompanyModel.clear();
      dispatch([
        updateContactsState(() => ({
          editDetailsFieldDialogState: {
            ...editDialogState,
            submitLabel: detailsFieldSubmitLabel(
              editDialogState.selectedCompanyModel.selected.length,
            ),
            isSubmitDisabled: true,
            alertMessage: {
              key: "contacts.company-roles.details-alert-message",
            },
          },
        })),
      ]);
    },
  };
};

export const onUpdateContactsPersonalField = ({
  dispatch,
  editDialogState,
  portfolio,
}: {
  dispatch: EventDispatcher;
  editDialogState: EditDetailsFieldDialogState;
  portfolio: SelectedPortfolioOption;
}) => {
  const { selected } = editDialogState.selectedCompanyModel;

  const roles = compact(selected.map(aggregatedContactsUrnReference));
  const body: UpdatePersonField = {
    field: editDialogState.detailsFieldModel.type,
    value:
      editDialogState.detailsFieldModel.type === "phone"
        ? editDialogState.detailsFieldModel.updateModel.number
        : editDialogState.detailsFieldModel.updateModel.validatedValue,
    countryCode:
      editDialogState.detailsFieldModel.type === "phone"
        ? editDialogState.detailsFieldModel.updateModel.selectedCountry?.callingCode
        : undefined,
    roles,
  };

  return dispatch(
    !validateDetailsFieldDialog(editDialogState)
      ? []
      : [
          updateContactsState(() => ({
            editDetailsFieldDialogState: {
              ...editDialogState,
              isUpdating: true,
            },
          })),
          portfolioApi.updatePersonField({
            body,
            onSuccess: () => [
              updateContactsState(() => ({
                editDetailsFieldDialogState: undefined,
              })),
              showGeneralSuccess(),
              ...initContactsPage(portfolio),
            ],
            onFailure: () => [
              updateContactsState(() => ({
                editDetailsFieldDialogState: undefined,
              })),
              showError({ key: "general-error" }, { autoHide: true }),
            ],
          }),
        ],
  );
};

const aggregatedContactsUrnReference = (item: AggregatedCompanyItem): string => {
  return item.itemType === "role"
    ? toCompanyRoleReference(item.id)
    : toShareholderReference(item.id);
};

export const filterCompanies = (
  roleId: string | number,
  roles: AggregatedContactItem[],
): CompanyItem[] => {
  return roles
    .filter((role) => role.roleItems.some((x) => x.id === roleId))
    .flatMap((role) => role.roleItems?.map((item) => item.company) ?? []);
};

const prepContactDetailsTabs = ({
  dispatch,
  roleItems,
  ownershipItems,
  portfolio,
  tab,
}: {
  dispatch: EventDispatcher;
  roleItems: RoleItem[];
  ownershipItems: OwnershipItem[];
  portfolio: SelectedPortfolioOption;
  tab: AggregatedContactDetailsTab;
}): TabProps[] => {
  return [
    {
      id: "investments",
      label: {
        key: "contacts.details.investments",
        params: {
          count: ownershipItems.filter((x) => hasShares(x.shareholder)).length,
        },
      },
      onSelect: () => {
        when(ownershipItems[0]?.shareholder?.id, (shareholderId) =>
          dispatch([
            {
              type: "navigate",
              url: extractAggregatedShareholderDetailsUrl(portfolio, shareholderId),
            },
          ]),
        );
      },
      isActive: tab === "shareholder",
      disabled: ownershipItems.length === 0,
    },
    ...(ownershipItems.some((x) => x.shareholder.isCompany)
      ? []
      : [
          {
            id: "boardPositions",
            label: {
              key: "contacts.company-roles.details-board-positions",
              params: { count: roleItems.length },
            } satisfies TMessage,
            onSelect: () => {
              when(roleItems[0]?.role?.companyroleId, (companyroleId) =>
                dispatch([
                  {
                    type: "navigate",
                    url: extractAggregatedCompanyRoleDetailsUrl(portfolio, companyroleId),
                  },
                ]),
              );
            },
            isActive: tab === "companyRole",
            disabled: roleItems.length === 0,
          },
        ]),
  ];
};

const prepEditDialog = ({
  dispatch,
  mainItem,
  state,
  portfolio,
}: {
  dispatch: EventDispatcher;
  mainItem?: RoleItem;
  state?: ContactsState;
  portfolio: SelectedPortfolioOption;
}): RolesDialogsProps | undefined => {
  return !mainItem || !state?.form
    ? undefined
    : {
        settingsDialog: {
          state: RolesDialogState.EDIT,
          form: state.form,
          showPeriod: false,
          showResidentialAddress: false,
        },
        isCompanyRoleLoading: state.isSaving ?? false,
        onCloseDialog: () =>
          dispatch([
            updateContactsState(() => ({
              form: undefined,
            })),
          ]),
        onSubmitDialog: () => {
          if (state.form?.validateOnEdit()) {
            dispatch(saveCompanyRoleDetails({ mainItem, form: state.form, portfolio }));
          }
        },
        companyName:
          mainItem.role.companyroleId === state.form?.companyroleId
            ? mainItem.company.companyName
            : "",
      };
};

const closeEditDetailsDialog = (): DCEvent =>
  updateContactsState(() => ({
    form: undefined,
    isSaving: undefined,
  }));

const saveCompanyRoleDetails = ({
  mainItem,
  form,
  portfolio,
}: {
  mainItem: RoleItem;
  form: RoleForm;
  portfolio: SelectedPortfolioOption;
}): DCEvent[] => [
  updateContactsState(() => ({
    isSaving: true,
  })),
  companySettingsApi.updateCompanyRole(mainItem.company.organisationNumber, form.applyForm(), {
    onSuccess: () => [
      portfolioApi.getCompanyRoles({
        portfolio,
        onSuccess: (res) => [
          updateContactsState(() => ({ response: ok(res.data) })),
          ...initContactsPage(portfolio),
          closeEditDetailsDialog(),
          showGeneralSuccess(),
        ],
        onFailure: () => [closeEditDetailsDialog(), showGeneralError()],
      }),
    ],
    onFailure: () => [closeEditDetailsDialog(), showGeneralError()],
  }),
];

const prepInfoTags = (aggregate: AggregatedContactItem): InfoTagProps[] => {
  const birthDate =
    aggregate.roleItems.find((r) => r.role.birthDate)?.role.birthDate ??
    aggregate.ownershipItems.find(
      (o) => !o.shareholder.isCompany && o.shareholder.birthDateOrOrganisationNumber,
    )?.shareholder.birthDateOrOrganisationNumber;
  return compact([
    birthDate && {
      id: "birthDate",
      icon: "user-square",
      text: { type: "date", value: birthDate },
    },
  ]);
};

const lastLogin = (aggregate?: AggregatedContactItem): string | undefined => {
  const roleDates =
    aggregate?.roleItems
      ?.map((r) => r.role.lastLogin)
      .filter(isNotBlank)
      .map(toDate) ?? [];
  const ownershipDates =
    aggregate?.ownershipItems
      ?.map((o) => o.shareholder.lastLogin)
      .filter(isNotBlank)
      .map(toDate) ?? [];
  const allDates = [...roleDates, ...ownershipDates];
  const latest = max(allDates);
  return latest ? latest.toISOString() : undefined;
};

function prepContactDetailsTable({
  tab,
  aggregated,
  openEditDialog,
  navigateToBoardMembers,
  onNavigateToShareholder,
  language,
  shareSums,
  filter,
  investmentStatus,
  shareTypes,
}: {
  tab: AggregatedContactDetailsTab;
  aggregated:
    | {
        roleItems?: RoleItem[];
        ownershipItems?: OwnershipItem[];
      }
    | undefined;
  openEditDialog: (row: RoleItem) => void;
  navigateToBoardMembers: (row: RoleItem) => void;
  onNavigateToShareholder: (shareholderId: number) => void;
  language: Language;
  shareSums: AggregatedShareSummary[];
  filter: string;
  investmentStatus: InvestmentStatus;
  shareTypes: ShareType[];
}): DataTableProps<RoleItem> | DataTableProps<OwnershipItem> {
  if (tab === "companyRole") {
    return boardDataTable({
      openEditDialog,
      navigateToBoardMembers,
      roleItems: aggregated?.roleItems,
      filter,
    });
  } else {
    return ownershipDataTable({
      language,
      shareSums,
      ownershipItems: aggregated?.ownershipItems,
      onNavigateToShareholder,
      filter,
      investmentStatus,
      shareTypes,
    });
  }
}

const boardDataTable = ({
  openEditDialog,
  navigateToBoardMembers,
  roleItems,
  filter,
}: {
  openEditDialog: (row: RoleItem) => void;
  navigateToBoardMembers: (row: RoleItem) => void;
  roleItems?: RoleItem[];
  filter: string;
}): DataTableProps<RoleItem> => {
  const notFilterValue = () => undefined;
  return {
    tableColumns: [
      {
        name: "companyName",
        label: { key: "contacts.company-roles.details-company" },
        align: "left",
        getValue: (row) => row.company.companyName,
        getSortValue: (row) => row.company.companyName,
        getFilterValue: (row) => row.company.companyName,
      },
      {
        name: "role",
        label: { key: "role" },
        align: "left",
        getValue: (row) => companyRoleLabel(row.role),
        getSortValue: (row) => companyRoleLabel(row.role),
        getFilterValue: (row) => companyRoleLabel(row.role),
      },
      {
        name: "nationality",
        label: { key: "settings.nationality" },
        align: "left",
        getValue: (row, _, { t }) =>
          row.role.nationality
            ? row.role.nationality
            : createElement("div", { className: "subtle" }, t("not-set")),
        getSortValue: (row) => row.role.nationality,
        getFilterValue: notFilterValue,
      },
      {
        name: "countryOfResidence",
        label: { key: "contacts.company-roles.country" },
        align: "left",
        getValue: (row, _, { t }) =>
          row.role.residentialAddress?.country
            ? row.role.residentialAddress?.country
            : createElement("div", { className: "subtle" }, t("not-set")),
        getSortValue: (row) => row.role.residentialAddress?.country,
        getFilterValue: notFilterValue,
      },
      {
        name: "email",
        label: { key: "email" },
        align: "left",
        getValue: (row) =>
          isBlank(row.role.email)
            ? ([
                "link",
                {
                  label: { key: "contacts.company-roles.details.add-email" },
                  onClick: () => openEditDialog(row),
                },
              ] satisfies ["link", LinkButtonProps])
            : row.role.email,
        getSortValue: (row) => row.role.email,
        getFilterValue: (row) => row.role.email,
      },
      {
        name: "phoneNumber",
        label: { key: "contacts.company-roles.phone-number" },
        align: "left",
        getValue: (row) => formatPhoneNumber(row.role.phoneNumber),
        getSortValue: (row) => formatPhoneNumber(row.role.phoneNumber),
      },
    ],
    tableData: roleItems ?? [],
    getId: (row) => row.id,
    initialSortBy: "companyName",
    initialSortDirection: "asc",
    onRowClick: navigateToBoardMembers,
    rowActions: (row) => [
      [
        "context-menu",
        [
          {
            type: "item",
            label: { key: "contacts.company-roles.details-edit" },
            icon: "pen",
            onSelect: () => openEditDialog(row),
          },
          {
            type: "item",
            label: {
              key: "contacts.company-roles.details-view-board",
              params: { name: row.company.companyName },
            },
            icon: "users-three",
            onSelect: () => navigateToBoardMembers(row),
          },
        ],
      ] satisfies ["context-menu", DropdownMenuItems],
    ],
    filter,
  };
};

const ownershipDataTable = ({
  language,
  shareSums,
  ownershipItems,
  onNavigateToShareholder,
  filter,
  investmentStatus,
  shareTypes,
}: {
  language: Language;
  shareSums: AggregatedShareSummary[];
  onNavigateToShareholder: (shareholderId: number) => void;
  ownershipItems?: OwnershipItem[];
  filter: string;
  investmentStatus: InvestmentStatus;
  shareTypes: ShareType[];
}): DataTableProps<OwnershipItem> => {
  return {
    tableColumns: [
      {
        name: "companyName",
        label: { key: "contacts.company-roles.details-company" },
        align: "left",
        getClassName: () => "whitespace-nowrap",
        getValue: (row) => row.company.companyName,
        getSortValue: (row) => row.company.companyName,
        getFilterValue: (row) => row.company.companyName,
      },
      {
        name: "email",
        label: { key: "email" },
        align: "left",
        getValue: (row) => row.shareholder.email,
        getSortValue: (row) => row.shareholder.email,
        getFilterValue: (row) => row.shareholder.email,
      },
      ...ownershipShareColumns(shareTypes, shareSums, ownershipItems),
      {
        name: "totalshares",
        label: { key: "contacts.company-roles.details.total-shares" },
        align: "right",
        getValue: (row) =>
          formatNumber(language, totalNumberOfSharesOfShareholder(row.shareholder)),
        getSortValue: (row) => totalNumberOfSharesOfShareholder(row.shareholder),
      },
      {
        name: "percentageShares",
        label: { key: "ownership.table.ownership" },
        align: "right",
        getValue: (row) =>
          formatPercent(
            language,
            percentOf(
              totalNumberOfSharesOfShareholder(row.shareholder),
              totalNumberOfShares(shareSums, row.shareholder.organizationNumber),
            ),
          ),
        getSortValue: (row) =>
          percentOf(
            totalNumberOfSharesOfShareholder(row.shareholder),
            totalNumberOfShares(shareSums, row.shareholder.organizationNumber),
          ),
      },
      {
        name: "totalVotes",
        label: { key: "transactions.number-of-votes" },
        align: "right",
        getValue: (row) =>
          isOwnShareholder(row.shareholder)
            ? ([
                "info",
                {
                  infoText: { key: "transactions.own-shares-info" },
                },
              ] satisfies ["info", InfoButtonProps])
            : formatNumber(language, totalNumberOfVotesForShareholder(row.shareholder)),
        getSortValue: (row) => totalNumberOfVotesForShareholder(row.shareholder),
      },
      {
        name: "percentageVotes",
        label: { key: "transactions.percentage-of-votes" },
        align: "right",
        getValue: (row) =>
          formatPercent(
            language,
            percentOf(
              totalNumberOfVotesForShareholder(row.shareholder),
              totalNumberOfVotes(shareSums, row.shareholder.organizationNumber),
            ),
          ),
        getSortValue: (row) =>
          percentOf(
            totalNumberOfVotesForShareholder(row.shareholder),
            totalNumberOfVotes(shareSums, row.shareholder.organizationNumber),
          ),
      },
    ],
    tableData: ownershipItems ? filterOwnershipByStatus(investmentStatus, ownershipItems) : [],
    getId: (row) => row.id,
    initialSortBy: "companyName",
    initialSortDirection: "asc",
    onRowClick: (row) => onNavigateToShareholder(row.shareholder.id),
    rowActions: (row) => [
      [
        "context-menu",
        [
          {
            type: "item",
            label: { key: "portfolioContacts.viewShareholder" },
            icon: "user-list",
            onSelect: () => onNavigateToShareholder(row.shareholder.id),
          },
        ],
      ] satisfies ["context-menu", DropdownMenuItems],
    ],
    filter,
  };
};

const ownershipShareColumns = (
  shareTypes: ShareType[],
  shareSums: AggregatedShareSummary[],
  ownershipItems?: OwnershipItem[],
): TableColumn<OwnershipItem>[] => {
  if (!ownershipItems) return [];
  const sumOfShares = shareSums.filter((x) =>
    ownershipItems?.find((s) => x.orgNo === s.shareholder.organizationNumber),
  );
  return Object.values(ShareTypeCode)
    .filter((x) => shareTypes.some((s) => s.shareTypeCode === x))
    .flatMap((shareType) => createShareColumns(shareType, shareTypes, sumOfShares));
};

const createShareColumns = (
  shareType: ShareTypeCode,
  shareTypes: ShareType[],
  shareSums: AggregatedShareSummary[],
): TableColumn<OwnershipItem>[] => [
  {
    name: shareType,
    label: shareTypeLabel(shareType),
    align: "right",
    getValue: (row) =>
      numberOfSharesColumnValue({
        sharetypeCode: shareType,
        ownershipItem: row,
        shareTypes,
      }),
    getSortValue: (row) => {
      const shares = row.shareholder.data.find(
        (x) => x.sharetypeCode === shareType,
      )?.numberOfShares;
      return shares ?? 0;
    },
  },
  {
    name: `${shareType}-percent`,
    label: "%",
    align: "right",
    getValue: (row) =>
      sharesPercentageColumnValue({
        sharetypeCode: shareType,
        ownershipItem: row,
        shareTypes,
        shareSums,
      }),
    getSortValue: (row) => {
      const shareholderShare = row.shareholder.data.find((f) => f.sharetypeCode === shareType);
      const totalNumberOfShares = shareSums.find(
        (s) =>
          s.sharetypeId === shareholderShare?.shareTypeId &&
          s.orgNo === row.company.organisationNumber,
      )?.numberOfShares;

      return totalNumberOfShares ?? 0;
    },
    getClassName: () => "subtle",
  },
];

const numberOfSharesColumnValue = ({
  sharetypeCode,
  ownershipItem,
  shareTypes,
}: {
  sharetypeCode: string;
  ownershipItem: OwnershipItem;
  shareTypes: ShareType[];
}): TableColumnValue => {
  const shareTypeMeta = shareTypes.find((x) => x.shareTypeCode === sharetypeCode);
  const item = ownershipItem.shareholder.data.find((x) => x.sharetypeCode === sharetypeCode);
  if (shareTypeMeta?.organizationnumber === ownershipItem.company.organisationNumber && !item) {
    return { translated: "0" };
  }
  const numberOfShares = item ? item.numberOfShares : 0;
  if (!item) {
    return [
      "info",
      {
        icon: "info",
        infoText: {
          key: "contacts.company-roles.details.not-share-class-info",
          params: {
            companyName: ownershipItem.company.companyName,
          },
        },
      },
    ];
  }
  return {
    type: "number",
    value: numberOfShares,
  };
};

const sharesPercentageColumnValue = ({
  sharetypeCode,
  ownershipItem,
  shareSums,
}: {
  sharetypeCode: string;
  ownershipItem: OwnershipItem;
  shareTypes: ShareType[];
  shareSums: AggregatedShareSummary[];
}): TableColumnValue | undefined => {
  const item = ownershipItem.shareholder.data.find((x) => x.sharetypeCode === sharetypeCode);
  if (!item) {
    return undefined;
  }
  const totalNumberOfShares =
    shareSums.find(
      (s) =>
        s.sharetypeId === item.shareTypeId && s.orgNo === ownershipItem.company.organisationNumber,
    )?.numberOfShares ?? 0;
  if (totalNumberOfShares === 0) {
    return undefined;
  }
  return { type: "percent", value: item.numberOfShares / totalNumberOfShares };
};

function totalNumberOfShares(shares: AggregatedShareSummary[], orgNo: string): number {
  return shares.filter((f) => f.orgNo === orgNo).reduce((total, s) => total + s.numberOfShares, 0);
}

function totalNumberOfVotes(shares: AggregatedShareSummary[], orgNo: string): number {
  return shares.filter((f) => f.orgNo === orgNo).reduce((total, s) => total + s.numberOfVotes, 0);
}

const filterOwnershipByStatus = (status: InvestmentStatus, ownershipItems: OwnershipItem[]) => {
  switch (status) {
    case "all":
      return ownershipItems;
    case "current":
      return ownershipItems.filter((s) => hasShares(s.shareholder));
    case "former":
      return ownershipItems.filter((s) => !hasShares(s.shareholder));
  }
};

const makeRoleItem = (x: RoleItem): AggregatedCompanyItem => ({
  id: x.id,
  companyItem: x.company,
  subText: companyRoleLabel(x.role),
  itemType: "role",
});

const makeOwnershipItem = (x: OwnershipItem): AggregatedCompanyItem => ({
  id: Number(x.id),
  companyItem: x.company,
  subText: x.shareholder.isCompany
    ? { translated: getFullName(x.shareholder.contactInfo) }
    : { key: "contacts.details.shareholder" },
  itemType: "ownership",
});

export const editableCompanyItems = (
  aggregate: AggregatedContactItem,
  access: CompanyAccess,
): AggregatedCompanyItem[] => [
  ...aggregate.roleItems
    .filter((x) => roleForCompany(access, x.company.organisationNumber).allowCompanyRolesEdit)
    .map(makeRoleItem),
  ...aggregate.ownershipItems
    .filter((x) => roleForCompany(access, x.company.organisationNumber).allowShareholdersEdit)
    .map(makeOwnershipItem),
];

export const prepAggregatedContactDetailsDropdownMenu = ({
  dispatch,
  aggregate,
  isUpdating,
  access,
}: {
  dispatch: EventDispatcher;
  aggregate: AggregatedContactItem;
  isUpdating?: boolean;
  access: CompanyAccess;
}): DropdownMenuProps | undefined => {
  const options = editableCompanyItems(aggregate, access);
  if (options.length === 0) {
    return undefined;
  }

  const existingPhone =
    aggregate.roleItems.find((x) => x.role.phoneNumber)?.role.phoneNumber ??
    aggregate.ownershipItems.find((x) => x.shareholder.personInfo?.phoneNumber)?.shareholder
      .personInfo?.phoneNumber ??
    aggregate.ownershipItems.find((x) => x.shareholder.contactInfo?.phoneNumber)?.shareholder
      .contactInfo?.phoneNumber;

  return {
    button: {
      type: "button",
      variant: "secondary",
      label: { key: "contacts.company-roles.details-edit-all" },
      icon: "edit",
    },
    items: [
      { type: "label", label: { key: "contacts.company-roles.details-bulkEdit.selectField" } },
      ...mapToAggregatedMenuItems({
        dispatch,
        options,
        name: aggregate.name,
        isUpdating,
        hasCompanyShareholder: aggregate.ownershipItems.some((x) => x.shareholder.isCompany),
        existingPhone,
      }),
    ],
  };
};

const mapToAggregatedMenuItems = ({
  dispatch,
  options,
  name,
  isUpdating,
  hasCompanyShareholder,
  existingPhone,
}: {
  dispatch: EventDispatcher;
  options: AggregatedCompanyItem[];
  name: string;
  isUpdating?: boolean;
  hasCompanyShareholder: boolean;
  existingPhone?: PhoneNumber;
}): DropdownMenuItemProps[] => {
  return aggregatedDetailsFieldTypes
    .filter((type) => {
      if (type === "personName") return !hasCompanyShareholder;
      if (type === "contactInfoNationalId" || type === "contactInfoName")
        return hasCompanyShareholder;
      return true;
    })
    .map<DropdownMenuItemProps>((field) => {
      const { id, label, icon } = fieldMenuItem[field];
      return {
        type: "item",
        id,
        label,
        icon,
        onSelect: () =>
          openEditAggregatedContactsDialog({
            dispatch,
            options,
            name,
            aggregatedDetailsFieldType: field,
            isUpdating,
            existingPhone,
          }),
      };
    });
};

const openEditAggregatedContactsDialog = ({
  dispatch,
  name,
  options,
  aggregatedDetailsFieldType,
  isUpdating,
  existingPhone,
}: {
  dispatch: EventDispatcher;
  name: string;
  options: AggregatedCompanyItem[];
  aggregatedDetailsFieldType: AggregatedDetailsFieldType;
  isUpdating?: boolean;
  existingPhone?: PhoneNumber;
}) => {
  return dispatch([
    updateContactsState(() => ({
      editDetailsFieldDialogState: {
        title: {
          key: "contacts.company-roles.details.edit-dialog-title",
          params: {
            fieldName: {
              type: "uncapitilize",
              value: aggregatedDetailsFieldTypeLabel[aggregatedDetailsFieldType],
            },
            name,
          },
        },
        aggregatedDetailsFieldType,
        submitLabel: detailsFieldSubmitLabel(options.length),
        isUpdating,
        detailsFieldModel: detailsFieldModel(aggregatedDetailsFieldType, existingPhone),
        selectedCompanyModel: new OptionFieldModel<AggregatedCompanyItem>({
          options,
          idFn: (option) => String(option.id),
          labelFn: (option) => ({ translated: option.companyItem.companyName }),
          selectedIds: options.map((x) => String(x.id)),
          required: true,
        }),
      },
    })),
  ]);
};

const detailsFieldModel = (
  aggregatedDetailsFieldType: AggregatedDetailsFieldType,
  existingPhone?: PhoneNumber,
): PersonInfoField => {
  if (aggregatedDetailsFieldType === "phone") {
    return {
      type: "phone",
      updateModel: new PhoneInputModel({ required: true, value: existingPhone }),
    };
  }
  return {
    type: aggregatedDetailsFieldType,
    updateModel: new StringFieldModel({
      required: true,
      validator: (input) => validatorDetailsFieldInput(input, aggregatedDetailsFieldType),
    }),
  };
};

const validatorDetailsFieldInput = (
  inputValue: string,
  type: AggregatedDetailsFieldType,
): TMessage | undefined => {
  switch (type) {
    case "contactInfoName":
    case "personName":
      return validateName(inputValue);
    case "email":
      return emailValidator(inputValue);
  }
};

const detailsFieldSubmitLabel = (count: number): TMessage => {
  return {
    key: "contacts.company-roles.details.submit-label",
    params: {
      count,
    },
  };
};

const isDetailsFieldDialogValid = (dialogState: EditDetailsFieldDialogState) => {
  return (
    dialogState.detailsFieldModel.updateModel.isValid && dialogState.selectedCompanyModel.isValid
  );
};

const validateDetailsFieldDialog = (dialogState: EditDetailsFieldDialogState) => {
  dialogState.detailsFieldModel.updateModel.validate();
  dialogState.selectedCompanyModel.validate();
  return isDetailsFieldDialogValid(dialogState);
};

export const filterShareTypesByOwnership = (
  shareTypes: ShareType[],
  ownershipItems?: OwnershipItem[],
): ShareType[] => {
  return shareTypes.filter((x) =>
    ownershipItems?.some((o) => x.organizationnumber === o.shareholder.organizationNumber),
  );
};
